import { CommonModule } from '@angular/common';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit} from '@angular/core';
import {EventBusService} from 'src/app/services/event-bus.service';
import {ToursStateService} from 'src/app/services/tours-state.service';
import {environment} from "../../../environments/environment";
import {filter} from "rxjs/operators";
import {SearchService} from "../../services/search/search.service";
import {HOTEL_TOURS_PAGE_CONFIG, HotelToursPageConfig} from "../../config/hotel-tours-list-page.config";

type PaginationItem = number | 'ellipsis';

@Component({
  selector: 'app-pagination',
  templateUrl: environment.mobile ? './pagination.component.mobile.html' : './pagination.component.html',
  styleUrls: environment.mobile ? ['./pagination.component.mobile.scss'] : ['./pagination.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule]
})
export class PaginationComponent implements OnInit {
  private readonly PAGE_START_NUMBER = 1;

  currentPage = this.PAGE_START_NUMBER;
  pageCount = 0;
  maxButtonCount: number;
  pages: PaginationItem[] = [];

  isShowNextPrev = false;
  isPrevAvailable = false;
  isNextAvailable = false;

  messages: any = {};

  constructor(
      @Inject(HOTEL_TOURS_PAGE_CONFIG) appConfig: HotelToursPageConfig,
      private eventBus: EventBusService,
      private state: ToursStateService,
      private searchService: SearchService,
      private cdRef: ChangeDetectorRef,
  ) {
    this.maxButtonCount = appConfig.paginationMaxButtonCount;
    this.messages = appConfig.translates.pagination;
  }

  ngOnInit() {
    this.searchService.newSearchStarted$
        .pipe(
            filter(searchId => !!searchId)
        )
        .subscribe(() => {
          this.currentPage = this.PAGE_START_NUMBER;
          this.pageCount = 0;
        });

    this.searchService.pagesCount$.subscribe(pageCount => {
      this.pageCount = pageCount;

      this.calculatePages();
      this.updateNextPrev();

      this.cdRef.detectChanges();
    });

    this.state.filtersApplyed$.subscribe(() => {
      this.resetCurrentPage();
    });

    this.searchService.sortChanged$.subscribe(() => {
      this.resetCurrentPage();
    });

    this.searchService.filtersReset$.subscribe(() => {
      this.resetCurrentPage();
    });
  }

  onClick(page: number) {
    if (this.currentPage !== Number(page)) {
      this.currentPage = page;

      this.state.setPage(page);
      this.searchService.pageChanged();

      this.calculatePages();
      this.updateNextPrev();
    }
  }

  trackByPage(index: number, page: PaginationItem): string {
    return `${page}-${index}`;
  }

  onFirst() {
    if (this.currentPage === this.PAGE_START_NUMBER) {
      return;
    }

    this.currentPage = this.PAGE_START_NUMBER;
    this.state.setPage(this.currentPage);
    this.searchService.pageChanged();

    this.calculatePages();
    this.updateNextPrev();
  }

  onLast() {
    if (this.currentPage === this.pageCount) {
      return;
    }

    this.currentPage = this.pageCount;
    this.state.setPage(this.currentPage);
    this.searchService.pageChanged();

    this.calculatePages();
    this.updateNextPrev();
  }

  onPrev() {
    if (!this.isPrevAvailable) {
      return;
    }

    const prevPage = this.currentPage - 1;
    if (prevPage <= 1) {
      this.currentPage = 1;
    } else {
      this.currentPage = prevPage;
    }

    this.state.setPage(this.currentPage);
    this.searchService.pageChanged();

    this.calculatePages();
    this.updateNextPrev();
  }

  onNext() {
    if (!this.isNextAvailable) {
      return;
    }

    const nextPage = this.currentPage + 1;
    if (nextPage > this.pageCount) {
      this.currentPage = this.pageCount;
    } else {
      this.currentPage = nextPage;
    }

    this.state.setPage(this.currentPage);
    this.searchService.pageChanged();

    this.calculatePages();
    this.updateNextPrev();
  }

  private calculatePages(): void {
    this.pages = [];

    if (this.pageCount <= 1) {
      return;
    }

    if (environment.mobile) {
      this.calculateMobilePages();
      return;
    }

    const visibleWindowSize = Math.max(5, Number(this.maxButtonCount) || 5);
    const simplePageCount = visibleWindowSize + 2;

    if (this.pageCount <= simplePageCount) {
      for (let page = this.PAGE_START_NUMBER; page <= this.pageCount; page += 1) {
        this.pages.push(page);
      }

      return;
    }

    if (this.currentPage <= visibleWindowSize - 1) {
      for (let page = this.PAGE_START_NUMBER; page <= visibleWindowSize; page += 1) {
        this.pages.push(page);
      }

      this.pages.push('ellipsis', this.pageCount);
      return;
    }

    const lastWindowStart = this.pageCount - visibleWindowSize + 1;
    if (this.currentPage >= lastWindowStart + 1) {
      this.pages.push(this.PAGE_START_NUMBER, 'ellipsis');

      for (let page = lastWindowStart; page <= this.pageCount; page += 1) {
        this.pages.push(page);
      }

      return;
    }

    this.pages.push(this.PAGE_START_NUMBER, 'ellipsis');

    for (let page = this.currentPage - 1; page <= this.currentPage + 1; page += 1) {
      this.pages.push(page);
    }

    this.pages.push('ellipsis', this.pageCount);
  }

  private calculateMobilePages(): void {
    if (this.pageCount <= 3) {
      for (let page = this.PAGE_START_NUMBER; page <= this.pageCount; page += 1) {
        this.pages.push(page);
      }

      return;
    }

    this.pages.push(this.PAGE_START_NUMBER, this.PAGE_START_NUMBER + 1, 'ellipsis', this.pageCount);
  }

  private updateNextPrev() {
    this.isShowNextPrev = false;

    if (this.pageCount > this.maxButtonCount) {
      this.isShowNextPrev = true;
    }

    if (this.currentPage === this.PAGE_START_NUMBER) {
      this.isPrevAvailable = false;
      this.isNextAvailable = true;
    } else if (this.currentPage === this.pageCount) {
      this.isPrevAvailable = true;
      this.isNextAvailable = false;
    } else {
      this.isPrevAvailable = true;
      this.isNextAvailable = true;
    }
  }

  private resetCurrentPage(): void {
    this.currentPage = this.PAGE_START_NUMBER;
    this.calculatePages();
    this.updateNextPrev();
    this.cdRef.markForCheck();
  }
}
